import { useEffect, useState } from "react";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  CONJ_DGC_LAYER_NAME,
  DGC_REFERENCE_YEAR,
  DGC_YEARS,
  DgcYearRow,
  aggregateStateSeries,
  buildDgcSeries,
  getDgcColor,
} from "../../global/constants/dgcConjuntos";
import { useDgcChart } from "../../context/DgcChartContext";
import { geoServer } from "../../services/geoServer";
import { useErrorToast } from "../../hooks/useErrorToast";
import { COLOR_DEC_APURADO, COLOR_FEC_APURADO, COLOR_DEC_LIMITE, COLOR_FEC_LIMITE, COLOR_DGC } from './style'


const WORKSPACE = "pepe-atlas";

/** Campos pedidos ao WFS para o agregado estadual (sem a geometria). */
const AGGREGATION_FIELDS = DGC_YEARS.flatMap((year) => [
  `DGC_${year}`,
  `DEC_APU_${year}`,
  `DEC_LIM_${year}`,
  `FEC_APU_${year}`,
  `FEC_LIM_${year}`,
  `CC_MED_${year}`,
]).join(",");

function buildOptions(series: DgcYearRow[]): Highcharts.Options {
  return {
    chart: { type: "column", height: 420 },
    title: { text: "" },
    credits: { enabled: false },
    exporting: { enabled: false },
    xAxis: {
      categories: series.map((row) => String(row.year)),
      crosshair: true,
      title: { text: "Ano" },
    },
    // DEC (horas), FEC (número de interrupções) e DGC (índice adimensional)
    // têm escalas diferentes, então cada um recebe seu próprio eixo.
    yAxis: [
      { title: { text: "DEC (horas)" } },
      { title: { text: "FEC (interrupções)" }, opposite: true },
      {
        title: { text: "DGC" },
        opposite: true,
        gridLineWidth: 0,
        labels: { format: "{value:.2f}" },
      },
    ],
    tooltip: { shared: true, valueDecimals: 2 },
    plotOptions: {
      column: { borderWidth: 0, groupPadding: 0.12 },
      line: { marker: { enabled: true, radius: 3 }, lineWidth: 2 },
      series: { connectNulls: false },
    },
    series: [
      {
        type: "column",
        name: "DEC Apurado",
        color: COLOR_DEC_APURADO,
        yAxis: 0,
        data: series.map((row) => row.decApurado),
        tooltip: { valueSuffix: " h" },
      },
      {
        type: "column",
        name: "FEC Apurado",
        color: COLOR_FEC_APURADO,
        yAxis: 1,
        data: series.map((row) => row.fecApurado),
      },
      {
        type: "line",
        name: "DEC Limite",
        color: COLOR_DEC_LIMITE,
        yAxis: 0,
        data: series.map((row) => row.decLimite),
        tooltip: { valueSuffix: " h" },
      },
      {
        type: "line",
        name: "FEC Limite",
        color: COLOR_FEC_LIMITE,
        yAxis: 1,
        dashStyle: "ShortDash",
        data: series.map((row) => row.fecLimite),
      },
      {
        type: "line",
        name: "DGC",
        color: COLOR_DGC,
        yAxis: 2,
        lineWidth: 3,
        data: series.map((row) => row.dgc),
        tooltip: { valueDecimals: 3 },
      },
    ],
  };
}

/** Busca a camada inteira no WFS para montar o agregado estadual. */
function useEstadoSeries(enabled: boolean) {
  const errorToast = useErrorToast();
  const [series, setSeries] = useState<DgcYearRow[] | null>(null);
  const [conjuntos, setConjuntos] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!enabled) return;

    const controller = new AbortController();
    setIsLoading(true);

    geoServer
      .get(`${WORKSPACE}/wfs`, {
        params: {
          service: "WFS",
          version: "1.0.0",
          request: "GetFeature",
          typeName: `${WORKSPACE}:${CONJ_DGC_LAYER_NAME}`,
          outputFormat: "application/json",
          // só os atributos entram na conta; pedir sem a geometria corta a
          // resposta de ~600 KB para poucas dezenas
          propertyName: AGGREGATION_FIELDS,
        },
        signal: controller.signal,
      })
      .then((response) => {
        const features = response.data?.features ?? [];
        setConjuntos(features.length);
        setSeries(
          aggregateStateSeries(
            features.map((feature: any) => feature.properties ?? {})
          )
        );
      })
      .catch((error: Error) => {
        if (
          controller.signal.aborted ||
          error.name === "AbortError" ||
          error.name === "CanceledError"
        ) {
          return;
        }
        errorToast("Não foi possível carregar os dados do estado.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [enabled, errorToast]);

  return { series, conjuntos, isLoading };
}

export function ConjuntoDGCModal() {
  const { view, close } = useDgcChart();
  const isEstado = view?.type === "estado";
  const estado = useEstadoSeries(isEstado);

  if (!view) return null;

  const series =
    view.type === "conjunto" ? buildDgcSeries(view.properties) : estado.series;

  const title =
    view.type === "conjunto"
      ? view.properties.NOME ?? "Conjunto elétrico"
      : "Pernambuco — todos os conjuntos";

  const dgc =
    series?.find((row) => row.year === DGC_REFERENCE_YEAR)?.dgc ?? null;

  return (
    <Dialog open onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        {series ? (
          <>
            <div className="flex items-center gap-2 text-sm text-[#555]">
              <span
                className="inline-block h-3 w-3 rounded-sm border border-[#404040]"
                style={{ backgroundColor: getDgcColor(dgc) }}
              />
              <span>
                DGC {DGC_REFERENCE_YEAR}:{" "}
                <strong>{dgc === null ? "sem dados" : dgc.toFixed(3)}</strong>
                {isEstado && ` · ${estado.conjuntos} conjuntos`}
              </span>
            </div>

            <HighchartsReact highcharts={Highcharts} options={buildOptions(series)} />

            <p className="text-xs text-[#777]">
              DEC e FEC apurados (barras) frente aos limites regulatórios
              (linhas).
              {isEstado &&
                " Valores do estado agregados por média ponderada pelo número médio de unidades consumidoras de cada conjunto."}{" "}
              Fonte: ANEEL — indicadores de continuidade por conjunto elétrico.
            </p>
          </>
        ) : (
          <p className="py-16 text-center text-sm text-[#777]">
            {estado.isLoading
              ? "Carregando dados dos conjuntos…"
              : "Nenhum dado disponível."}
          </p>
        )}
      </DialogContent>
    </Dialog>
  );
}
